import axios, { AxiosInstance, AxiosRequestConfig, AxiosError } from 'axios';
import { BaseApiClient } from './BaseApiClient';
import { IApiClient, ApiResponse, ApiError, ApiClientConfig } from './IApiClient';
import { authService } from '../auth/AuthService';
import { getAnalyticsService } from '@core/contexts/ServiceContext.tsx';
import { IAnalyticsService } from '@core/interfaces';

export class ProductionApiClient extends BaseApiClient implements IApiClient {
    private client: AxiosInstance;
    private organisationId: string | null = null;
    private analyticsService: IAnalyticsService;

    constructor(config?: ApiClientConfig) {
        super();

        this.client = axios.create({
            baseURL: config?.baseURL || import.meta.env.VITE_API,
            timeout: config?.timeout || 30000,
            headers: {
                'Content-Type': 'application/json',
                ...config?.headers
            }
        });

        this.analyticsService = getAnalyticsService();
        this.setupInterceptors();
        console.log('[ProductionApiClient] Initialized');
    }

    // ============ HTTP Helpers Implementation ============

    protected async httpGet<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        try {
            const response = await this.client.get<T>(url, config);
            return this.createResponse<T>(response.data, response.status, response.statusText);
        } catch (error) {
            this.analyticsService.trackException(error, {
                httpMethod: 'GET',
                url,
                timestamp: new Date().toISOString()
            });
            throw await this.transformError(error as AxiosError);
        }
    }

    protected async httpPost<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        try {
            const response = await this.client.post<T>(url, data, config);
            return this.createResponse<T>(response.data, response.status, response.statusText);
        } catch (error) {
            this.analyticsService.trackException(error, {
                httpMethod: 'POST',
                url,
                data,
                timestamp: new Date().toISOString()
            });
            throw await this.transformError(error as AxiosError);
        }
    }

    protected async httpPut<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        try {
            const response = await this.client.put<T>(url, data, config);
            return this.createResponse<T>(response.data, response.status, response.statusText);
        } catch (error) {
            this.analyticsService.trackException(error, {
                httpMethod: 'PUT',
                url,
                data,
                timestamp: new Date().toISOString()
            });
            throw await this.transformError(error as AxiosError);
        }
    }

    protected async httpPatch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        try {
            const response = await this.client.patch<T>(url, data, config);
            return this.createResponse<T>(response.data, response.status, response.statusText);
        } catch (error) {
            this.analyticsService.trackException(error, {
                httpMethod: 'PATCH',
                url,
                data,
                timestamp: new Date().toISOString()
            });
            throw await this.transformError(error as AxiosError);
        }
    }

    protected async httpDelete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        try {
            const response = await this.client.delete<T>(url, config);
            return this.createResponse<T>(response.data, response.status, response.statusText);
        } catch (error) {
            this.analyticsService.trackException(error, {
                httpMethod: 'DELETE',
                url,
                timestamp: new Date().toISOString()
            });
            throw await this.transformError(error as AxiosError);
        }
    }

    setOrganisationId(orgId: string): void {
        this.organisationId = orgId;
    }

    // ============ Legacy Support (Deprecated) ============

    /** @deprecated Use specific domain methods instead */
    async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        return this.httpGet<T>(url, config);
    }

    /** @deprecated Use specific domain methods instead */
    async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        return this.httpPost<T>(url, data, config);
    }

    /** @deprecated Use specific domain methods instead */
    async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        return this.httpPut<T>(url, data, config);
    }

    /** @deprecated Use specific domain methods instead */
    async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        return this.httpPatch<T>(url, data, config);
    }

    /** @deprecated Use specific domain methods instead */
    async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
        return this.httpDelete<T>(url, config);
    }

    // ============ Interceptors Setup ============

    private setupInterceptors(): void {
        // Request interceptor — attach a fresh token from MSAL to every outgoing request.
        this.client.interceptors.request.use(
            async (config) => {
                if (this.organisationId && config.headers) {
                    config.headers['Organisation-Id'] = this.organisationId;
                }

                const token = await authService.getAccessTokenSilent();
                if (!token) {
                    return Promise.reject(new Error('No access token available'));
                }

                if (token && config.headers) {
                    config.headers.Authorization = `Bearer ${token}`;
                }

                return config;
            },
            (error) => {
                console.error('[ProductionApiClient] Request error:', error);
                return Promise.reject(error);
            }
        );

        this.client.interceptors.response.use(
            (response) => response,
            (error: AxiosError) => Promise.reject(error)
        );
    }

    private async transformError(error: AxiosError): Promise<ApiError> {
        const apiError = this.createError('An unexpected error occurred', error.response?.status);

        if (error.response?.data) {
            let responseData = error.response.data as any;

            if (responseData instanceof Blob) {
                try {
                    const text = await responseData.text();
                    responseData = JSON.parse(text);
                } catch (e) {
                    console.error('[ProductionApiClient] Failed to parse Blob error response:', e);
                }
            }

            apiError.message = responseData.message || apiError.message;
            apiError.properties = responseData.properties;
        } else if (error.message) {
            apiError.message = error.message;
        }

        return apiError;
    }
}
